import { Injectable } from '@angular/core';
import { RouteRequest, RouteResult, ROUTING_CONSTANTS } from './map-provider.service';
import { osService } from '../osService.service';

@Injectable({
  providedIn: 'root'
})
export class RouteConsistencyService {
  constructor(private osService: osService) {}

  /**
   * Normalise une requête de route pour assurer la cohérence
   */
  normalizeRouteRequest(request: RouteRequest): RouteRequest {
    return {
      ...request,
      travelMode: request.travelMode || ROUTING_CONSTANTS.TRAVEL_MODES.DRIVING,
      avoidHighways: request.avoidHighways ?? ROUTING_CONSTANTS.DEFAULT_OPTIONS.avoidHighways,
      avoidTolls: request.avoidTolls ?? ROUTING_CONSTANTS.DEFAULT_OPTIONS.avoidTolls,
      avoidFerries: request.avoidFerries ?? ROUTING_CONSTANTS.DEFAULT_OPTIONS.avoidFerries,
      optimizeWaypoints: request.optimizeWaypoints ?? ROUTING_CONSTANTS.DEFAULT_OPTIONS.optimizeWaypoints,
      unit: (request.unit as 'metric' | 'imperial') || ROUTING_CONSTANTS.DEFAULT_OPTIONS.unit
    };
  }

  /**
   * Normalise les coordonnées pour assurer la cohérence
   */
  normalizeCoordinates(position: any): { lat: number; lng: number } {
    return {
      lat: position.lat || position.latitude,
      lng: position.lng || position.longitude
    };
  }

  /**
   * Valide une requête de route
   */
  validateRouteRequest(request: RouteRequest): boolean {
    if (!request.origin || !request.destination) {
      return false;
    }
    
    const origin = this.normalizeCoordinates(request.origin);
    const destination = this.normalizeCoordinates(request.destination);

    if (
      !Number.isFinite(origin.lat) ||
      !Number.isFinite(origin.lng) ||
      !Number.isFinite(destination.lat) ||
      !Number.isFinite(destination.lng)
    ) {
      return false;
    }

    return true;
  }

  /**
   * Calcule la distance en ligne droite entre deux points (pour validation)
   */
  calculateDirectDistance(origin: any, destination: any): number {
    const originCoords = this.normalizeCoordinates(origin);
    const destCoords = this.normalizeCoordinates(destination);
    
    const R = 6371e3; // Rayon de la Terre en mètres
    const φ1 = originCoords.lat * Math.PI / 180;
    const φ2 = destCoords.lat * Math.PI / 180;
    const Δφ = (destCoords.lat - originCoords.lat) * Math.PI / 180;
    const Δλ = (destCoords.lng - originCoords.lng) * Math.PI / 180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c; // Distance en mètres
  }

  /**
   * Valide la cohérence d'un résultat de route
   */
  validateRouteResult(result: RouteResult, origin: any, destination: any): boolean {
    const directDistance = this.calculateDirectDistance(origin, destination);
    const routeDistance = (result.distance.Km * 1000) + result.distance.M;
    
    // La distance de route ne devrait pas être inférieure à la distance en ligne droite
    if (routeDistance < directDistance * 0.8) {
      return false;
    }
    
    // La distance de route ne devrait pas être plus de 3x la distance en ligne droite
    if (routeDistance > directDistance * 3) {
      return false;
    }
    
    return true;
  }

  /**
   * Enregistre une requête de route
   */
  logRouteRequest(request: RouteRequest, provider: string): void {
    // Logging désactivé en production
  }

  /**
   * Enregistre un résultat de route
   */
  logRouteResult(result: RouteResult, provider: string): void {
    // Logging désactivé en production
  }

  /**
   * Vérifie si un composant utilise les services centralisés
   */
  checkCentralizedServiceUsage(componentName: string, methodName: string, isUsingCentralized: boolean): void {
    // Vérification désactivée en production
  }

  /**
   * Génère un rapport de cohérence des routes
   */
  generateConsistencyReport(): void {
    // Rapport désactivé en production
  }

  /**
   * Test de cohérence des distances pour un trajet donné
   */
  async testDistanceConsistency(origin: any, destination: any): Promise<void> {
    // Test désactivé en production
  }
} 